import './connector.css'

import {
	IonButton,
	IonButtons,
	IonContent,
	IonHeader,
	IonIcon,
	IonItem,
	IonList,
	IonModal,
	IonSearchbar,
	IonToolbar,
} from '@ionic/react'
import {
	QueryClient,
	QueryClientProvider,
	useQuery,
} from '@tanstack/react-query'
import { matchSorter } from 'match-sorter'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { Selector } from './EditField'
import { fetchJson, formatDate } from './util'
import { useDisableSync, useEnableSync, useRemoveSkipMatchId, useUnlockGame } from './functions'
import {
	useMyRelevantConnector,
	useMyRelevantTeam,
	useMyGames,
	useNewSeasonConnectorState,
	useSetShouldSyncFixture,
	useShowTeamSelection,
} from './data'
import netballConnectImage from './media/netball-connect.png'
import playHqImage from './media/playhq.png'
import { IonSearchbarCustomEvent, SearchbarInputEventDetail } from '@ionic/core'
import { cloudDoneOutline, openOutline } from 'ionicons/icons'
import {
	NetballConnectTeamCard,
	NetballConnectUrlPicker,
} from './NetballConnectSelector'
import {
	fetchOrganisations,
	fetchDivisionsAndTeams,
	FlatTeamEntry,
	isSelectableNetballConnectTeamName,
} from './ncApi'
import {
	NcSetupSelection,
	NcCachedCompetitionWithKey,
	getCachedCompetitions,
	contributeToCache,
	selectionToConnectorTeam,
	isCacheFresh,
	getCurrentUserId,
} from './ncCache'
import { NcResolvedResult } from './netballConnectResolver'
import {
	PlayHqSelector,
	PlayHqTeamCard,
} from './PlayHqSelector'
import {
	fetchPlayHqFixtureGames,
	isPlayHqNativeTransportAvailable,
} from './playHqApi'
import { FixtureSyncGame } from './fixtureSync'
import {
	SyncManagement,
	SyncManagementItem,
} from './SyncManagement'

const queryClient = new QueryClient()
const CACHEABLE_NETBALL_CONNECT_ORG_TYPE_REF_IDS = new Set([3])
const PLAYHQ_WEB_UNSUPPORTED_MESSAGE = 'Sorry! PlayHQ / NetballHQ fixture sync isn\'t supported via a web browser. Please use the AutoRoster app instead.'

function isCacheableLeagueOrganisation(organisation: NetballConnectOrganization) {
	// Verified against Squadi's public organisations payload: 3 = league/association, 4 = club.
	return CACHEABLE_NETBALL_CONNECT_ORG_TYPE_REF_IDS.has(organisation.organisationTypeRefId)
}

function flattenCompetitionTeams(competitions: NcCachedCompetitionWithKey[]) {
	const teams: FlatTeamEntry[] = []
	const seenTeamEntries = new Set<string>()

	for (const competition of competitions) {
		for (const [divId, div] of Object.entries(competition.divisions || {})) {
			for (const [teamId, teamData] of Object.entries(div.teams || {})) {
				if (!isSelectableNetballConnectTeamName(teamData.teamName)) {
					continue
				}

				const entry = {
					teamId: Number(teamId),
					teamName: teamData.teamName,
					logoUrl: teamData.logoUrl,
					divId: Number(divId),
					divName: div.divName,
					competitionId: competition.competitionId,
					compName: competition.compName,
					competitionKey: competition.competitionKey,
				}
				const entryKey = queryFlatTeamEntryKey(entry)
				if (!seenTeamEntries.has(entryKey)) {
					seenTeamEntries.add(entryKey)
					teams.push(entry)
				}
			}
		}
	}

	return teams.sort((a, b) => a.teamName.localeCompare(b.teamName))
}

function queryFlatTeamEntryKey(entry: FlatTeamEntry) {
	return `${entry.competitionKey}-${entry.divId}-${entry.teamId}`
}

type ConnectorProps = {
	show: boolean
	onDismiss: () => void
	selectionMode: 'draft' | 'current'
}

const Connector = (props: ConnectorProps) => {

	const { show, onDismiss, selectionMode } = props

	const modal = useRef<HTMLIonModalElement>(null)

	return (
		<IonModal
			ref={modal}
			isOpen={show}
			onWillDismiss={onDismiss}
		>
			<IonHeader>
				<IonToolbar>
					<IonButtons slot="end">
						<IonButton onClick={() => modal.current?.dismiss()}>
							Done
						</IonButton>
					</IonButtons>
				</IonToolbar>
			</IonHeader>
			<IonContent>
				<QueryClientProvider client={queryClient}>
					<ConnectorContent
						selectionMode={selectionMode}
						onDismiss={onDismiss}
					/>
				</QueryClientProvider>
			</IonContent>
		</IonModal>
	)
}

type ConnectorContentProps = {
	onDismiss: () => void
	selectionMode: 'draft' | 'current'
}

const ConnectorContent = (props: ConnectorContentProps) => {
	const { onDismiss, selectionMode } = props

	const relevantConnector = useMyRelevantConnector()
	const [draftConnector] = useNewSeasonConnectorState()

	const connector = selectionMode === 'draft' ? draftConnector : relevantConnector

	return (
		<div className="connector-content">
			<h1>
				Fixture Sync (Beta) <IonIcon icon={cloudDoneOutline} />
			</h1>
			<p>
				Let AutoRoster import your fixture and keep it up to date.
			</p>
			{
				connector ? (
					<DisplayConnector
						connector={connector}
						onDismiss={onDismiss}
						selectionMode={selectionMode}
					/>
				) : (
					<SelectConnector selectionMode={selectionMode} />
				)
			}
		</div>
	)
}

type DisplayConnectorProps = {
	connector: Connector
	onDismiss: () => void
	selectionMode: 'draft' | 'current'
}

const DisplayConnector = (props: DisplayConnectorProps) => {
	const { connector, onDismiss, selectionMode } = props

	const disableSync = useDisableSync()
	const removeSkipMatchId = useRemoveSkipMatchId()
	const setShouldSyncFixture = useSetShouldSyncFixture()
	const [, setNewConnector] = useNewSeasonConnectorState()
	const unlockGame = useUnlockGame()

	const games = useMyGames()
	const showTeamSelection = useShowTeamSelection()

	const netballConnectFixturePreviewQuery = useQuery({
		queryKey: ['matches', connector?.params?.competitionId, connector?.params?.id],
		enabled: connector?.type === 'NetballConnect',
		queryFn: () => connector.type === 'NetballConnect'
			? fetchJson<NetballConnectMatches>(`https://api-netball.squadi.com/livescores/round/matches?competitionId=${connector.params.competitionId}&teamIds=[${connector.params.id}]&ignoreStatuses=[1]`)
			: Promise.reject(new Error('Unsupported connector')),
	})

	const playHqFixturePreviewQuery = useQuery({
		queryKey: ['playhq-matches', connector?.type === 'PlayHQ' ? connector.params.gradeId : undefined, connector?.params?.id],
		enabled: connector?.type === 'PlayHQ' && isPlayHqNativeTransportAvailable(),
		queryFn: () => connector.type === 'PlayHQ'
			? fetchPlayHqFixtureGames(connector)
			: Promise.reject(new Error('Unsupported connector')),
	})

	const nextRound = netballConnectFixturePreviewQuery.data?.rounds?.[0]
	const nextMatch = nextRound?.matches?.[0]

	if (connector.type === 'NetballConnect' && !nextMatch && !netballConnectFixturePreviewQuery.isFetching) {
		console.warn('No match found for connector', connector?.id)
	}

	const selectedTeamPreview = (connector.type === 'NetballConnect' && nextMatch)
		? (nextMatch.team1Id === connector.params.id ? nextMatch.team1 : nextMatch.team2)
		: undefined
	const logoUrl = selectedTeamPreview?.logoUrl

	const canSyncOnThisPlatform = connector.type !== 'PlayHQ' || isPlayHqNativeTransportAvailable()
	const syncNowButton = selectionMode === 'draft' || showTeamSelection || !canSyncOnThisPlatform ? null : (
		<IonButton
			className="sync-now-button"
			size="small"
			onClick={() => {
				setShouldSyncFixture('user')
				onDismiss()
			}}
		>
			Sync now
		</IonButton>
	)
	const syncManagementItems = selectionMode === 'current' ? queryConnectorSyncManagementItems({
		connector,
		netballConnectFixtureData: netballConnectFixturePreviewQuery.data,
		playHqFixtureData: playHqFixturePreviewQuery.data,
		games,
		onUnlock: unlockGame,
		onUnignore: removeSkipMatchId,
	}) : []

	const stopSyncButton = (
		<IonButton
			className="nc-sync-button"
			color="danger"
			fill="clear"
			size="default"
			expand="block"
			slot="end"
			onClick={() => {
				if (selectionMode === 'draft') {
					setNewConnector(null)
					return
				}
				disableSync()
			}}
		>
			{selectionMode === 'draft' ? 'Clear Selection' : 'Stop Syncing'}
		</IonButton>
	)

	const content = (connector?.type === 'NetballConnect') ? (
		<div className="connector">
			<div className="sync-enabled-line">
				<span>
					<IonIcon icon={cloudDoneOutline} className="sync-enabled" />
					Sync enabled via
				</span>
				<img className="netball-connect-image" src={netballConnectImage} alt="NetballConnect" />
				{syncNowButton}
			</div>
			<NetballConnectTeamCard
				teamName={connector.params.teamName}
				logoUrl={logoUrl}
				divName={connector.params.divName}
				compName={connector.params.compName}
			/>
			{
				netballConnectFixturePreviewQuery.isFetching ? (
					<p>
						Checking...
					</p>
				) : netballConnectFixturePreviewQuery.isError ? (
					<p>
						Error fetching fixture data: {describeQueryError(netballConnectFixturePreviewQuery.error)}
					</p>
				) : netballConnectFixturePreviewQuery.isPending ? (
					<p>
						Loading fixture data...
					</p>
				) : nextMatch ? (
					<p>
						Online fixture data looks good: Found {netballConnectFixturePreviewQuery.data?.rounds?.length} rounds.
					</p>
				) : (
					<p>
						No fixture data found for this team.
					</p>
				)
			}
			<SyncManagement items={syncManagementItems} />
			{stopSyncButton}
		</div>
	) : connector?.type === 'PlayHQ' ? (
		<div className="connector">
			<div className="sync-enabled-line">
				<span>
					<IonIcon icon={cloudDoneOutline} className="sync-enabled" />
					Sync enabled via
				</span>
				<img className="playhq-image" src={playHqImage} alt="PlayHQ" />
				{syncNowButton}
			</div>
			<PlayHqTeamCard
				teamName={connector.params.teamName}
				gradeName={connector.params.gradeName}
				compName={connector.params.compName}
				logoUrl={connector.params.logoUrl}
				seasonName={connector.params.seasonName}
			/>
			{
				!isPlayHqNativeTransportAvailable() ? (
					<p className="connector-error">
						{PLAYHQ_WEB_UNSUPPORTED_MESSAGE}
					</p>
				) : playHqFixturePreviewQuery.isFetching ? (
					<p>
						Checking...
					</p>
				) : playHqFixturePreviewQuery.isError ? (
					<p>
						Error fetching fixture data: {describeQueryError(playHqFixturePreviewQuery.error)}
					</p>
				) : playHqFixturePreviewQuery.isPending ? (
					<p>
						Loading fixture data...
					</p>
				) : playHqFixturePreviewQuery.data?.length ? (
					<p>
						Online fixture data looks good: Found {playHqFixturePreviewQuery.data.length} games.
					</p>
				) : (
					<p>
						No fixture data found for this team.
					</p>
				)
			}
			<SyncManagement items={syncManagementItems} />
			{stopSyncButton}
		</div>
	) : (
		<div>
			Unknown Connector
		</div>
	)

	return (
		<div className="connector-content">
			{content}
		</div>
	)
}

function queryConnectorSyncManagementItems(props: {
	connector: Connector
	netballConnectFixtureData?: NetballConnectMatches
	playHqFixtureData?: FixtureSyncGame[]
	games: Games | null
	onUnlock: (gameId: GameId) => void | Promise<void>
	onUnignore: (matchId: ConnectorMatchId) => void | Promise<void>
}) {
	const { connector, netballConnectFixtureData, playHqFixtureData, games, onUnlock, onUnignore } = props

	const lockedGameItems: SyncManagementItem[] = Object.values(games ?? { })
		.filter(game => game.syncLock)
		.map(game => ({
			date: game.date,
			round: game.round,
			key: `locked-${game.id}`,
			label: `Round ${game.round} vs ${game.opponent}`,
			onAction: () => onUnlock(game.id),
			actionLabel: 'Allow sync',
		}))

	const skippedMatchIds = new Set(connector.skipMatchIds ?? [])
	let skippedFixtureItems: SyncManagementItem[] = []
	if (connector.type === 'NetballConnect') {
		skippedFixtureItems = (netballConnectFixtureData?.rounds ?? [])
			.flatMap(round => round.matches.map(match => ({ round, match })))
			.filter(({ match }) => skippedMatchIds.has(match.id))
			.map(({ match }) => ({
				match,
				opponent: String(match.team1Id === connector.params.id ? match.team2.name : match.team1.name).trim(),
				round: String(match.round.name).replace('Round ', ''),
			}))
			.filter(({ opponent }) => opponent !== 'Bye')
			.map(({ match, opponent, round }) => {
				return {
					date: new Date(match.startTime).getTime(),
					round,
					key: `skipped-${match.id}`,
					label: `Round ${round} vs ${opponent} (official fixture ignored)`,
					onAction: () => onUnignore(match.id),
					actionLabel: 'Allow sync',
				}
			})
	} else if (connector.type === 'PlayHQ') {
		skippedFixtureItems = (playHqFixtureData ?? [])
			.filter(match => skippedMatchIds.has(match.id))
			.map(match => ({
				date: match.date,
				round: match.round,
				key: `skipped-${match.id}`,
				label: `Round ${match.round} vs ${match.opponent} (official fixture ignored)`,
				onAction: () => onUnignore(match.id),
				actionLabel: 'Allow sync',
			}))
	}

	return [
		...lockedGameItems,
		...skippedFixtureItems,
	]
}

type SelectConnectorProps = {
	selectionMode: 'draft' | 'current'
}

type ConnectorProvider = 'NetballConnect' | 'PlayHQ'

const SelectConnector = (props: SelectConnectorProps) => {
	const { selectionMode } = props

	const relevantTeam = useMyRelevantTeam()
	const canUsePlayHq = isPlayHqNativeTransportAvailable()

	const firstYear = 2019
	const currentYear = new Date().getFullYear()

	const [selectedProvider, setSelectedProvider] = useState<ConnectorProvider | null>(null)
	const [selectedYear, setSelectedYear] = useState(currentYear)
	const [selectedOrgKey, setSelectedOrgKey] = useState<string>()
	const [selectedOrgName, setSelectedOrgName] = useState<string>()
	const [selectedTeam, setSelectedTeam] = useState<NcSetupSelection>()

	const [matchingOrganisations, setMatchingOrganisations] = useState<NetballConnectOrganizations>()
	const [availableTeams, setAvailableTeams] = useState<FlatTeamEntry[]>()
	const [visibleTeams, setVisibleTeams] = useState<FlatTeamEntry[]>()
	const [requiresUrlFallback, setRequiresUrlFallback] = useState(false)
	const [isLoadingAvailableTeams, setIsLoadingAvailableTeams] = useState(false)
	const [hasResolvedFallbackUrl, setHasResolvedFallbackUrl] = useState(false)
	const [showFallbackInstructions, setShowFallbackInstructions] = useState(true)

	const enableSync = useEnableSync()
	const [, setNewConnector] = useNewSeasonConnectorState()

	const organisationSearchRef = useRef<HTMLIonSearchbarElement>(null)
	const teamSearchRef = useRef<HTMLIonSearchbarElement>(null)
	const loadRequestIdRef = useRef(0)
	const pendingFallbackRefreshRef = useRef<{ orgKey: string; orgName: string } | null>(null)

	const yearCount = currentYear - firstYear + 2
	const yearOptions = Array(yearCount).fill(null).map((_, index) => ({ value: firstYear + index }))

	const handleYearInput = (e: CustomEvent) => {
		const target = e.target as HTMLInputElement
		setSelectedYear(Number(target.value))
	}

	// Fetch league/association organisations (public)
	const organisationsQuery = useQuery({
		queryKey: ['organizations'],
		enabled: selectedProvider === 'NetballConnect',
		queryFn: async () => {
			const organisations = await fetchOrganisations()
			const leagueOrganisations = organisations.filter(isCacheableLeagueOrganisation)
			console.log('fetchOrganisations:', {
				totalOrganisationCount: organisations.length,
				cacheableLeagueCount: leagueOrganisations.length,
			})
			return leagueOrganisations
		},
	})

	const clearSelectedOrganisation = useCallback(() => {
		pendingFallbackRefreshRef.current = null
		setSelectedOrgKey(undefined)
		setSelectedOrgName(undefined)
		setMatchingOrganisations(undefined)
		setAvailableTeams(undefined)
		setVisibleTeams(undefined)
		setSelectedTeam(undefined)
		setRequiresUrlFallback(false)
		setHasResolvedFallbackUrl(false)
		setShowFallbackInstructions(true)
	}, [])

	const filterMatchingOrganisations = (value: string) => {
		if (!organisationsQuery.data) return
		const lcValue = value?.toLowerCase()
		const matching = organisationsQuery.data.filter(d => !value || d.name.toLowerCase().indexOf(lcValue) !== -1)
		const sortedMatching = matchSorter(matching, value, { keys: ['name'] })
		setMatchingOrganisations(sortedMatching.slice(0, 50))
	}

	const refreshStaleCompetitionsInBackground = useCallback(async (
		loadId: number,
		selectedOrgKey: string,
		selectedOrgName: string,
		competitions: NcCachedCompetitionWithKey[],
	) => {
		const userId = getCurrentUserId()
		const staleCompetitions = competitions.filter(comp =>
			!isCacheFresh(comp.updatedAt, userId, comp.competitionKey)
		)

		await Promise.all(staleCompetitions.map(async comp => {
			try {
				const { divisions } = await fetchDivisionsAndTeams(
					comp.competitionKey,
					comp.competitionId,
					comp.compName,
				)

				if (loadId !== loadRequestIdRef.current) {
					return
				}

				void contributeToCache({
					orgKey: selectedOrgKey,
					orgName: selectedOrgName,
					competitionKey: comp.competitionKey,
				}, {
					competitionId: comp.competitionId,
					compName: comp.compName,
					year: comp.year,
					updatedAt: Date.now(),
					divisions,
				})
			} catch (err) {
				console.warn(`Failed to refresh stale competition ${comp.competitionKey}:`, err)
			}
		}))
	}, [])

	// When org is selected, load cached competitions immediately and refresh stale ones later.
	const loadTeamsForSelectedOrg = useCallback(async (selectedOrgKey: string, selectedOrgName: string) => {
		const loadId = ++loadRequestIdRef.current

		setIsLoadingAvailableTeams(true)
		setRequiresUrlFallback(false)
		setAvailableTeams(undefined)
		setVisibleTeams(undefined)

		try {
			const cached = await getCachedCompetitions(selectedOrgKey)
			const cachedCompetitionsForYear = cached.filter(c => c.year === selectedYear)

			if (loadId !== loadRequestIdRef.current) {
				return
			}

			if (cachedCompetitionsForYear.length === 0) {
				setRequiresUrlFallback(true)
				return
			}

			setAvailableTeams(flattenCompetitionTeams(cachedCompetitionsForYear))
			void refreshStaleCompetitionsInBackground(
				loadId,
				selectedOrgKey,
				selectedOrgName,
				cachedCompetitionsForYear,
			)
		} catch (err) {
			console.error('Failed to load teams for org:', err)
			setRequiresUrlFallback(true)
		} finally {
			if (loadId === loadRequestIdRef.current) {
				setIsLoadingAvailableTeams(false)
			}
		}
	}, [refreshStaleCompetitionsInBackground, selectedYear])

	const filterVisibleTeams = useCallback((value?: string) => {
		if (!availableTeams) return

		let sorted: FlatTeamEntry[]
		if (value) {
			sorted = matchSorter(availableTeams, value, { keys: ['teamName'] })
		} else if (relevantTeam?.name) {
			sorted = matchSorter(availableTeams, relevantTeam.name, { keys: ['teamName'] })
			if (!sorted.length) {
				sorted = availableTeams
			}
		} else {
			sorted = availableTeams
		}

		setVisibleTeams(sorted.slice(0, 50))
	}, [availableTeams, relevantTeam])

	const openNetballConnectForFallback = () => {
		if (!selectedOrgKey) return

		const yearId = selectedYear - 2018
		window.open(
			`https://registration.netballconnect.com/liveScoreSeasonFixture?organisationKey=${selectedOrgKey}&yearId=${yearId}`,
			'_blank',
			'noopener,noreferrer',
		)
	}

	useEffect(() => {
		setSelectedTeam(undefined)
		setAvailableTeams(undefined)
		setVisibleTeams(undefined)
		setRequiresUrlFallback(false)
		setHasResolvedFallbackUrl(false)
		setShowFallbackInstructions(true)

		if (!selectedOrgKey || !selectedOrgName) {
			loadRequestIdRef.current += 1
			setIsLoadingAvailableTeams(false)
			return
		}

		void loadTeamsForSelectedOrg(selectedOrgKey, selectedOrgName)
	}, [loadTeamsForSelectedOrg, selectedOrgKey, selectedOrgName, selectedYear])

	useEffect(() => {
		if (availableTeams && !visibleTeams) {
			filterVisibleTeams()
		}
	}, [availableTeams, visibleTeams, filterVisibleTeams])

	const isPending = organisationsQuery.isPending || isLoadingAvailableTeams

	useEffect(() => {
		let timer: ReturnType<typeof setTimeout> | undefined
		if (selectedProvider === 'NetballConnect' && !selectedOrgKey && !isPending) {
			timer = setTimeout(() => {
				void organisationSearchRef.current?.setFocus()
			}, 80)
		}
		return () => {
			if (timer) {
				clearTimeout(timer)
			}
		}
	}, [isPending, selectedProvider, selectedOrgKey])

	// Match preview for selected team
	const previewMatchesQuery = useQuery({
		queryKey: ['matches', selectedTeam?.competitionId, selectedTeam?.teamId],
		enabled: !!selectedTeam,
		queryFn: () => fetchJson<NetballConnectMatches>(
			`https://api-netball.squadi.com/livescores/round/matches?competitionId=${selectedTeam!.competitionId}&teamIds=[${selectedTeam!.teamId}]&ignoreStatuses=[1]`,
		),
	})

	const handleSelectOrganisation = (organisation: NetballConnectOrganization) => {
		setSelectedOrgKey(organisation.organisationUniqueKey)
		setSelectedOrgName(organisation.name)
		setMatchingOrganisations(undefined)
	}

	const handleSelectTeam = (entry: FlatTeamEntry | undefined) => {
		if (!entry || !selectedOrgKey || !selectedOrgName) {
			setSelectedTeam(undefined)
			return
		}
		setSelectedTeam({
			orgKey: selectedOrgKey,
			orgName: selectedOrgName,
			competitionKey: entry.competitionKey,
			competitionId: entry.competitionId,
			compName: entry.compName,
			divId: entry.divId,
			divName: entry.divName,
			teamId: entry.teamId,
			teamName: entry.teamName,
			logoUrl: entry.logoUrl,
		})
	}

	const handleUseSelectedTeam = async () => {
		if (!selectedTeam) return

		const connectorTeam = selectionToConnectorTeam(selectedTeam)
		const connector = connectorFromTeam(connectorTeam)

		if (selectionMode === 'draft') {
			setNewConnector(connector)
			return
		}

		await enableSync(connector)
	}

	const handleResolvedFallbackResult = useCallback(async (result: NcResolvedResult) => {
		console.log('handleResolvedFallbackResult: optimistic cache write', {
			orgKey: result.selection.orgKey,
			competitionKey: result.selection.competitionKey,
			year: result.competitionData.year,
		})

		await contributeToCache(result.selection, result.competitionData)

		if (selectedOrgKey && selectedOrgKey === result.selection.orgKey && selectedOrgName) {
			pendingFallbackRefreshRef.current = {
				orgKey: selectedOrgKey,
				orgName: selectedOrgName,
			}
		}
	}, [selectedOrgKey, selectedOrgName])

	const handleUrlFallbackResolved = async (result: NcResolvedResult) => {
		const connectorTeam = selectionToConnectorTeam(result.selection)
		const connector = connectorFromTeam(connectorTeam)

		if (selectionMode === 'draft') {
			setNewConnector(connector)
			return
		}

		await enableSync(connector)
	}

	const error = organisationsQuery.error
	const teamsToShow = visibleTeams ?? []
	const showTeamPicker = Boolean(
		selectedOrgKey && !requiresUrlFallback && !isLoadingAvailableTeams && visibleTeams
	)

	useEffect(() => {
		let timer: ReturnType<typeof setTimeout>
		if (showTeamPicker && !selectedTeam) {
			timer = setTimeout(() => teamSearchRef.current?.setFocus(), 80)
		}
		return () => clearTimeout(timer)
	}, [showTeamPicker, selectedTeam])

	const renderFallbackSetupCard = (introText: React.ReactNode) => (
		<div className="nc-setup-card">
			{
				!hasResolvedFallbackUrl ? (
					showFallbackInstructions ? (
						<>
							<p>
								{introText}
							</p>

							<ol className="nc-setup-instructions">
								<li>
									<p>
										Open NetballConnect and use the top filters to select
										your <b>division</b> <em>and</em> your <b>team</b>.
									</p>
									<IonButton
										expand="block"
										fill="outline"
										onClick={openNetballConnectForFallback}
									>
										<IonIcon icon={openOutline} slot="start" />
										Find Team on NetballConnect
									</IonButton>
								</li>
								<li>
									<p>
										Copy the page URL.
									</p>
								</li>
								<li>
									<p>
										Paste the URL below.
									</p>
								</li>
							</ol>
						</>
					) : (
						<IonButton
							fill="clear"
							size="small"
							className="nc-show-instructions-button"
							onClick={() => setShowFallbackInstructions(true)}
						>
							Show Instructions
						</IonButton>
					)
				) : null
			}

			<NetballConnectUrlPicker
				actionLabel={selectionMode === 'draft' ? 'Use This Team' : 'Enable Online Sync'}
				onResolvedResult={handleResolvedFallbackResult}
				onUseTeam={handleUrlFallbackResolved}
				onResolvedChange={resolved => {
					setHasResolvedFallbackUrl(resolved)
					if (resolved) {
						setShowFallbackInstructions(false)
						return
					}

					const pendingRefresh = pendingFallbackRefreshRef.current
					pendingFallbackRefreshRef.current = null
					if (
						pendingRefresh
						&& selectedOrgKey === pendingRefresh.orgKey
						&& selectedOrgName === pendingRefresh.orgName
					) {
						void loadTeamsForSelectedOrg(pendingRefresh.orgKey, pendingRefresh.orgName)
					}
				}}
				fallbackYear={selectedYear}
			/>
		</div>
	)

	return (
		<div className="select-connector">
			<div className="connector-provider-picker">
				<IonButton
                    className={selectedProvider === 'NetballConnect' ? 'selected' : selectedProvider ? 'unselected' : ''}
                    fill="outline"
					size="small"
					onClick={() => setSelectedProvider('NetballConnect')}
				>
					<img className="connector-provider-image netball-connect-image" src={netballConnectImage} alt="NetballConnect" />
				</IonButton>
				<IonButton
                    className={selectedProvider === 'PlayHQ' ? 'selected' : selectedProvider ? 'unselected' : ''}
                    fill="outline"
					size="small"
					onClick={() => setSelectedProvider('PlayHQ')}
				>
					<img className="connector-provider-image playhq-image" src={playHqImage} alt="PlayHQ / NetballHQ" />
				</IonButton>
			</div>

            {
                (selectedProvider === 'NetballConnect' && error) ? (
                    <p>
                        Error: ${describeQueryError(error)}
                    </p>
                ) : null
            }

            {
                selectedProvider === null ? null :
                    selectedProvider === 'PlayHQ' ? (
                        canUsePlayHq ? (
                            <PlayHqSelector selectionMode={selectionMode} />
                        ) : (
                            <p className="connector-error">
                                {PLAYHQ_WEB_UNSUPPORTED_MESSAGE}
                            </p>
                        )
                ) : (
                isPending ? (
                    <p className="is-pending">
                        Contacting NetballConnect...
                    </p>
                ) : (
                    <IonList>
                        {
                            !hasResolvedFallbackUrl ? (
                                <>
                                    <IonItem>
                                        <Selector
                                            label="Year"
                                            value={selectedYear}
                                            options={yearOptions}
                                            onIonInput={handleYearInput}
                                        />
                                    </IonItem>

                                    {
                                        selectedOrgKey ? (
                                            <IonItem onClick={clearSelectedOrganisation}>
                                                <div>
                                                    League
                                                </div>
                                                <div slot="end">
                                                    {selectedOrgName}
                                                </div>
                                            </IonItem>
                                        ) : (
                                            <>
                                                <SearchBar
                                                    placeholder="League..."
                                                    onText={text => filterMatchingOrganisations(text)}
                                                    onEnter={() => {
                                                        if (matchingOrganisations?.length) {
                                                            handleSelectOrganisation(matchingOrganisations[0])
                                                        }
                                                    }}
                                                    ref={organisationSearchRef}
                                                />
                                                <IonList lines="none">
                                                    {
                                                        matchingOrganisations?.map(organisation => (
                                                            <IonItem key={organisation.organisationUniqueKey}>
                                                                <IonButton
                                                                    fill="outline"
                                                                    size="default"
                                                                    onClick={e => {
                                                                        e.preventDefault()
                                                                        e.stopPropagation()
                                                                        handleSelectOrganisation(organisation)
                                                                    }}
                                                                >
                                                                    {organisation.name}
                                                                </IonButton>
                                                            </IonItem>
                                                        ))
                                                    }
                                                </IonList>
                                            </>
                                        )
                                    }
                                </>
                            ) : null
                        }

                        {
                            selectedOrgKey && isLoadingAvailableTeams ? (
                                <IonItem>
                                    <p>Loading teams...</p>
                                </IonItem>
                            ) : null
                        }

                        {
                            selectedOrgKey && requiresUrlFallback && !isLoadingAvailableTeams ? (
                                renderFallbackSetupCard('To set up Fixture Sync for this league, NetballConnect requires you to:')
                            ) : null
                        }

                        {
                            selectedTeam ? (
                                <IonItem
                                    onClick={() => {
                                        setSelectedTeam(undefined)
                                        filterVisibleTeams()
                                    }}
                                >
                                    <div>
                                        Team
                                    </div>
                                    <div slot="end">
                                        <NetballConnectTeamCard
                                            onClick={() => setSelectedTeam(undefined)}
                                            teamName={selectedTeam.teamName}
                                            logoUrl={selectedTeam.logoUrl}
                                            divName={selectedTeam.divName}
                                            compName={selectedTeam.compName}
                                        />
                                    </div>
                                </IonItem>
                            ) : showTeamPicker ? (
                                <>
                                    <SearchBar
                                        placeholder="Team..."
                                        onText={text => filterVisibleTeams(text)}
                                        onEnter={() => {
                                            if (teamsToShow.length) {
                                                handleSelectTeam(teamsToShow[0])
                                            }
                                        }}
                                        ref={teamSearchRef}
                                    />
                                    <IonList lines="none">
                                        {
                                            teamsToShow.length === 0 ? (
                                                <IonItem>
                                                    No teams found.
                                                </IonItem>
                                            ) : teamsToShow.map(entry => (
                                                <IonItem key={queryFlatTeamEntryKey(entry)}>
                                                    <NetballConnectTeamCard
                                                        onClick={(e: React.MouseEvent) => {
                                                            e.preventDefault()
                                                            e.stopPropagation()
                                                            handleSelectTeam(entry)
                                                        }}
                                                        compName={entry.compName}
                                                        divName={entry.divName}
                                                        teamName={entry.teamName}
                                                        logoUrl={entry.logoUrl}
                                                    />
                                                </IonItem>
                                            ))
                                        }
                                    </IonList>
                                    {
                                        renderFallbackSetupCard(
                                            <>
                                                <b>Can&apos;t find your team?</b> To set up Fixture Sync for another team in this league:
                                            </>,
                                        )
                                    }
                                </>
                            ) : null
                        }

                        {
                            (selectedTeam && previewMatchesQuery.data) ? (
                                <div className="nc-matches-list">
                                    {
                                        previewMatchesQuery.data.rounds.map(round => {
                                            if (!round.matches.length) return null
                                                const [match] = round.matches
                                            const selectedSide = `team${match.team1Id === selectedTeam.teamId ? 1 : 2}` as const
                                            const opponentSide = selectedSide === 'team1' ? 'team2' : 'team1'
                                            return (
                                                <div
                                                    className="nc-match"
                                                    key={match.id}
                                                >
                                                    <div className="field-round">
                                                        <div>
                                                            {match.round?.name}
                                                        </div>
                                                    </div>
                                                    <div className="field-opponent">
                                                        {match[opponentSide]?.name}
                                                    </div>
                                                    <div className="field-date">
                                                        {formatDate(match.startTime)}
                                                    </div>
                                                    &bull;
                                                    <div className="field-venue">
                                                        {match.venueCourt?.name}
                                                    </div>
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                            ) : null
                        }

                        {
                            selectedTeam ? (
                                <IonButton
                                    className="nc-sync-button"
                                    size="default"
                                    expand="block"
                                    slot="end"
                                    onClick={() => {
                                        void handleUseSelectedTeam()
                                    }}
                                >
                                    {selectionMode === 'draft' ? 'Use This Team' : 'Enable Online Sync'}
                                </IonButton>
                            ) : null
                        }
                    </IonList>
                ))
            }
        </div>
    )
}

type SearchBarProps = {
	placeholder: string
	onText: (text: string) => void
	onEnter: () => void
}

const SearchBar = React.forwardRef<HTMLIonSearchbarElement, SearchBarProps>((props: SearchBarProps, ref) => {
	const { placeholder, onText, onEnter } = props

	return (
		<IonSearchbar
			ref={ref}
			placeholder={placeholder}
			onIonInput={(e: IonSearchbarCustomEvent<SearchbarInputEventDetail>) => {
				const searchbar = e.target as HTMLIonSearchbarElement | null
				onText(e.detail.value ?? searchbar?.value ?? '')
			}}
			onKeyDown={e => {
				if (e.code === 'Enter' || e.code === 'NumpadEnter') {
					onEnter()
				}
			}}
		/>
	)
})
SearchBar.displayName = 'SearchBar'

function connectorFromTeam(connectorTeam: ConnectorTeam): Connector {
	return {
		type: 'NetballConnect',
		id: 0,
		params: connectorTeam,
	}
}

function describeQueryError(error: unknown) {
	if (error instanceof Error) {
		return error.message
	}

	if (typeof error === 'string') {
		return error
	}

	return 'Unknown error'
}

export default Connector
